import type { AdminRecord } from '@/types/record'
import type { ColumnDef } from '@/types/page'
import { fieldLabel } from '@/utils/presentation'
import { formatCellValue } from '@/utils/format'

function csvCell(value: unknown): string {
  const text = String(value ?? '')
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

export function rowsToCsv(
  rows: AdminRecord[],
  columns: ColumnDef[],
  statusMap: Record<string, { label: string; tone?: string }> = {},
): string {
  const header = columns.map(column => csvCell(column.label)).join(',')
  const body = rows.map(row => columns.map(column => {
    const raw = row[column.key]
    const value = Array.isArray(raw) ? raw.join('、') : raw
    return csvCell(formatCellValue(value, row, { columnType: column.type, suffix: column.suffix, options: column.options, statusMap }))
  }).join(','))
  return `\uFEFF${[header, ...body].join('\r\n')}`
}

export function downloadBlob(filename: string, content: string, mime = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  window.setTimeout(() => URL.revokeObjectURL(url), 800)
}

export function parseCsv(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let quoted = false
  const input = text.replace(/^\uFEFF/, '')
  for (let index = 0; index < input.length; index += 1) {
    const char = input[index]
    const next = input[index + 1]
    if (quoted) {
      if (char === '"' && next === '"') {
        cell += '"'
        index += 1
      } else if (char === '"') {
        quoted = false
      } else {
        cell += char
      }
    } else if (char === '"') {
      quoted = true
    } else if (char === ',') {
      row.push(cell)
      cell = ''
    } else if (char === '\n') {
      row.push(cell.replace(/\r$/, ''))
      if (row.some(item => item.trim())) rows.push(row)
      row = []
      cell = ''
    } else {
      cell += char
    }
  }
  if (cell || row.length) {
    row.push(cell.replace(/\r$/, ''))
    if (row.some(item => item.trim())) rows.push(row)
  }
  return rows
}

export function labelForExport(key: string, columns: ColumnDef[]): string {
  return columns.find(column => column.key === key)?.label ?? fieldLabel(key)
}
